import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MDBBtn,
  MDBContainer,
  MDBInput,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBSpinner,
} from "mdb-react-ui-kit";
import {
  formatDisplayOrderNumber,
  formatOrderItemPriceUsd,
  getOrderDateValue,
} from "../utils/orderCurrency";
import PageWrapper from "../components/Common/PageWrapper";
import { useMessageBox } from "../components/Common/MessageBox";
import { useAuth } from "../context/AuthContext";
import { normalizeRole } from "../utils/rolePermissions";
import {
  fetchOrders,
  updateOrderStatus,
  getUserId,
  SELLER_TABS,
  OUTBOUND_TABS,
  isOrderDelivered,
  isOrderCancelled,
  fetchTrackingHistory,
} from "../services/ordersApi";
import "./OrdersPage.css";

const DEFAULT_VENDOR_NAME = "Not uploaded by ThriftOps";

function getItemVendorName(item) {
  return item.uploaded_by_name ?? item.uploadedBy?.name ?? DEFAULT_VENDOR_NAME;
}

const WORKFLOW_STEPS = [
  { key: "seller", label: "Vendor", field: "seller_status" },
  { key: "rider", label: "Rider", field: "rider_status" },
  { key: "qc", label: "QC", field: "qc_status" },
  { key: "outbound", label: "Outbound", field: "outbound_status" },
];

function formatDateTime(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}


function needsTrackingId(order) {
  return order.outbound_status === "dispatched" || isOrderDelivered(order);
}


function statusClass(status) {
  const s = String(status || "pending").toLowerCase();
  if (["approved", "ready_to_pick", "picked", "packed", "dispatched"].includes(s)) {
    return "status-success";
  }
  if (["rejected", "not_available"].includes(s)) {
    return "status-danger";
  }
  return "status-pending";
}

function formatStatus(status) {
  if (!status) return "Pending";
  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function hasMarketplaceStatus(order) {
  return !!(
    order.marketplace_order_status ||
    order.marketplace_payment_status ||
    order.marketplace_cancel_status ||
    order.marketplace_payment_hold_status ||
    order.marketplace_payment_method ||
    order.marketplace_shipping_carrier ||
    order.marketplace_shipping_service ||
    order.marketplace_last_modified ||
    order.marketplace_payment_reference_id ||
    order.marketplace_fulfillment_href
  );
}

function marketplaceStatusClass(type, value) {
  const s = String(value || "").toLowerCase();
  if (!value) return "status-neutral";

  if (type === "payment") {
    if (["paid", "completed", "succeeded"].includes(s)) return "status-success";
    if (["pending", "held", "in_progress"].includes(s)) return "status-pending";
    if (["failed", "refunded", "declined", "unpaid"].includes(s)) return "status-danger";
  }

  if (type === "order") {
    if (["fulfilled", "completed", "shipped", "delivered"].includes(s)) {
      return "status-success";
    }
    if (["cancelled", "canceled"].includes(s)) return "status-danger";
    if (["not_started", "pending", "in_progress", "partially_fulfilled"].includes(s)) {
      return "status-pending";
    }
  }

  if (type === "cancel") {
    if (s.includes("none") || s === "none_requested") return "status-success";
    if (s.includes("requested") || s.includes("pending")) return "status-pending";
    if (s.includes("completed") || s.includes("approved")) return "status-danger";
  }

  if (type === "hold") {
    if (s === "held") return "status-pending";
    if (s === "released" || s === "none") return "status-success";
  }

  return "status-neutral";
}
function getEvriTrackingUrl(fulfillmentHref) {
  if (!fulfillmentHref) return null;

  // Tracking ID is the last path segment of the eBay fulfillment URL
  // e.g. .../shipping_fulfillment/H051JA0180354720 -> H051JA0180354720
  const parts = fulfillmentHref.split("/").filter(Boolean);
  const trackingId = parts[parts.length - 1];

  if (!trackingId) return null;

  return `https://www.evri.com/track/parcel/${trackingId}/details`;
}

function MarketplaceOrderStatus({ order, showDetails = false }) {
  if (!hasMarketplaceStatus(order)) return null;

  const detailRows = showDetails
    ? [
        { label: "Payment Method", value: order.marketplace_payment_method },
        { label: "Shipping Carrier", value: order.marketplace_shipping_carrier },
        { label: "Shipping Service", value: order.marketplace_shipping_service },
        {
          label: "Payment Reference",
          value: order.marketplace_payment_reference_id,
        },
      ].filter((row) => row.value)
    : [];

  return (
    <div className="marketplace-status-panel">
      <div className="marketplace-status-header">
        <span className="marketplace-status-title">Marketplace Status</span>
        {order.marketplace_last_modified && (
          <span className="marketplace-status-updated">
            Last sync: {formatDateTime(order.marketplace_last_modified)}
          </span>
        )}
      </div>

      <div className="marketplace-status-badges">
        {order.marketplace_order_status && (
          <div className="marketplace-status-item">
            <span className="pipeline-label">Order</span>
            <span
              className={`pipeline-badge ${marketplaceStatusClass("order", order.marketplace_order_status)}`}
            >
              {formatStatus(order.marketplace_order_status)}
            </span>
          </div>
        )}
        {order.marketplace_payment_status && (
          <div className="marketplace-status-item">
            <span className="pipeline-label">Payment</span>
            <span
              className={`pipeline-badge ${marketplaceStatusClass("payment", order.marketplace_payment_status)}`}
            >
              {formatStatus(order.marketplace_payment_status)}
            </span>
          </div>
        )}
        {order.marketplace_cancel_status && (
          <div className="marketplace-status-item">
            <span className="pipeline-label">Cancel</span>
            <span
              className={`pipeline-badge ${marketplaceStatusClass("cancel", order.marketplace_cancel_status)}`}
            >
              {formatStatus(order.marketplace_cancel_status)}
            </span>
          </div>
        )}
        {order.marketplace_payment_hold_status && (
          <div className="marketplace-status-item">
            <span className="pipeline-label">Payment Hold</span>
            <span
              className={`pipeline-badge ${marketplaceStatusClass("hold", order.marketplace_payment_hold_status)}`}
            >
              {formatStatus(order.marketplace_payment_hold_status)}
            </span>
          </div>
        )}
      </div>

      {detailRows.length > 0 && (
        <div className="marketplace-status-details">
          {detailRows.map((row) => (
            <div key={row.label} className="marketplace-status-detail">
              <span className="marketplace-detail-label">{row.label}</span>
              <span className="marketplace-detail-value">{row.value}</span>
            </div>
          ))}
        </div>
      )}

      {showDetails && order.marketplace_fulfillment_href && (
        <a
          className="marketplace-status-link"
          href={getEvriTrackingUrl(order.marketplace_fulfillment_href)}
          target="_blank"
          rel="noopener noreferrer"
        >
          View fulfillment on marketplace
        </a>
      )}
    </div>
  );
}

function OrderStatusPipeline({ order }) {
  return (
    <div className="order-pipeline">
      {WORKFLOW_STEPS.map((step, index) => (
        <React.Fragment key={step.key}>
          <div className="pipeline-step">
            <span className="pipeline-label">{step.label}</span>
            <span
              className={`pipeline-badge ${statusClass(order[step.field])}`}
            >
              {formatStatus(order[step.field])}
            </span>
          </div>
          {index < WORKFLOW_STEPS.length - 1 && (
            <span className="pipeline-arrow">→</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function getItemAmountPayable(item, order) {
  if (item.amount_payable == null) return null;
  return formatOrderItemPriceUsd(item.amount_payable, order?.marketplace || order?.source);
}

function getItemRawPrice(item, order) {
  if (item.price == null) return null;
  return formatOrderItemPriceUsd(item.price, order?.marketplace || order?.source);
}

function escapeCsvValue(value) {
  if (value == null) return '""';
  const str = String(value).replace(/"/g, '""');
  return `"${str}"`;
}

function exportOrdersToCsv(orders, { isAdmin }) {
  const headers = [
    "Order ID",
    "Marketplace",
    "Order Date",
    "Vendor Status",
    "Rider Status",
    "QC Status",
    "Outbound Status",
    "Delivered",
    "Cancelled",
    "Tracking ID",
    "Marketplace Order Status",
    "Marketplace Payment Status",
    "Marketplace Cancel Status",
    "Payment Hold Status",
    "Payment Method",
    "Shipping Carrier",
    "Shipping Service",
    "SKU",
    "Product Title",
    "Quantity",
    "Amount Payable",
    ...(isAdmin ? ["Selling Price"] : []),
    "Vendor",
  ];

  const rows = [];

  orders.forEach((order) => {
    const items = order.items?.length ? order.items : [{}];

    items.forEach((item) => {
      const amountPayable = getItemAmountPayable(item, order) || "";
      const sellingPrice = isAdmin ? (getItemRawPrice(item, order) || "") : "";

      rows.push([
        formatDisplayOrderNumber(order.id),
        order.marketplace || order.source || "",
        order.created_at ? formatDateTime(order.created_at) : "",
        formatStatus(order.seller_status),
        formatStatus(order.rider_status),
        formatStatus(order.qc_status),
        formatStatus(order.outbound_status),
        isOrderDelivered(order) ? "Yes" : "No",
        isOrderCancelled(order) ? "Yes" : "No",
        order.courier_tracking_id || "",
        formatStatus(order.marketplace_order_status),
        formatStatus(order.marketplace_payment_status),
        formatStatus(order.marketplace_cancel_status),
        formatStatus(order.marketplace_payment_hold_status),
        order.marketplace_payment_method || "",
        order.marketplace_shipping_carrier || "",
        order.marketplace_shipping_service || "",
        item.sku || "",
        item.title || "",
        item.quantity || 1,
        amountPayable,
        ...(isAdmin ? [sellingPrice] : []),
        getItemVendorName(item),
      ]);
    });
  });

  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `orders_export_${new Date().toISOString().slice(0, 10)}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function ProductThumbnails({ items, onItemClick }) {
  if (!items?.length) {
    return <span className="text-muted">No items</span>;
  }

  return (
    <div className="order-items-preview">
      {items.slice(0, 4).map((item) => (
        <button
          key={item.id || item.product_id}
          type="button"
          className="order-item-thumb order-item-thumb-btn"
          onClick={() => onItemClick?.(item)}
          title={item.title || item.sku || "View product details"}
        >
          {item.image_url ? (
            <img src={item.image_url} alt={item.title || item.sku || "Product"} />
          ) : (
            <div className="order-item-placeholder">📦</div>
          )}
          <span className="order-item-qty">×{item.quantity || 1}</span>
        </button>
      ))}
      {items.length > 4 && (
        <span className="order-items-more">+{items.length - 4}</span>
      )}
    </div>
  );
}

function OrderProductModal({
  item,
  order,
  isAdmin,
  open,
  setOpen,
}) {
  if (!item) return null;

  const amountPayableValue = getItemAmountPayable(item, order);
  const rawPriceValue = getItemRawPrice(item, order);

  return (
    <MDBModal open={open} setOpen={setOpen} tabIndex="-1">
      <MDBModalDialog centered size="lg">
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>Product Details</MDBModalTitle>
            <MDBBtn
              className="btn-close"
              color="none"
              onClick={() => setOpen(false)}
            />
          </MDBModalHeader>
          <MDBModalBody>
            <div className="order-product-modal">
              <div className="order-product-modal-image">
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.title || item.sku || "Product"}
                  />
                ) : (
                  <div className="order-product-modal-placeholder">📦</div>
                )}
              </div>
              <div className="order-product-modal-details">
                <h4 className="order-product-modal-title">
                  {item.title || item.sku || `Product #${item.product_id}`}
                </h4>
                <dl className="order-product-modal-meta">
                  <div className="order-product-modal-row">
                    <dt>SKU</dt>
                    <dd>{item.sku || "—"}</dd>
                  </div>
                  <div className="order-product-modal-row">
                    <dt>Quantity</dt>
                    <dd>{item.quantity || 1}</dd>
                  </div>
                  {item.product_id != null && (
                    <div className="order-product-modal-row">
                      <dt>Product ID</dt>
                      <dd>{item.product_id}</dd>
                    </div>
                  )}
                  {amountPayableValue && (
                    <div className="order-product-modal-row">
                      <dt>Amount Payable</dt>
                      <dd>{amountPayableValue}</dd>
                    </div>
                  )}
                  {isAdmin && rawPriceValue && (
                    <div className="order-product-modal-row">
                      <dt>Selling Price</dt>
                      <dd>{rawPriceValue}</dd>
                    </div>
                  )}
                  <div className="order-product-modal-row">
                    <dt>Vendor</dt>
                    <dd>{getItemVendorName(item)}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </MDBModalBody>
          <MDBModalFooter>
            <MDBBtn color="secondary" onClick={() => setOpen(false)}>
              Close
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  );
}

export default function OrdersPage() {

  const { user, companyId } = useAuth();    
  const userId = getUserId(user);
  const userRole = normalizeRole(user?.role);

  const isSeller = userRole === "seller";
  const isRider = userRole === "rider";
  const isOutbound = userRole === "outbound";
  const isAdmin = userRole === "owner" || userRole === "admin";

  const [searchParams, setSearchParams] = useSearchParams();
  const statusTab = searchParams.get("tab") || (isAdmin ? "all" : "pending");
  const outboundTab = searchParams.get("view") || "qc_pending";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [marketplaceFilter, setMarketplaceFilter] = useState("");
  const [marketplaceStatusFilter, setMarketplaceStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductOrder, setSelectedProductOrder] = useState(null);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [dispatchOrderId, setDispatchOrderId] = useState(null);
  const [trackingId, setTrackingId] = useState("");
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);

  const { showSuccess, showError, MessageBoxComponent } = useMessageBox();

  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [trackingError, setTrackingError] = useState("");

  const openTrackingModal = async (trackingId) => {
    setTrackingModalOpen(true);
    setTrackingLoading(true);
    setTrackingData(null);
    setTrackingError("");
    try {
      const data = await fetchTrackingHistory(trackingId);
      setTrackingData(data);
    } catch (err) {
      setTrackingError(err.message || "Failed to load tracking info");
    } finally {
      setTrackingLoading(false);
    }
  };

  const pageTitle = useMemo(() => {
    if (isSeller) return "Vendor Queue";
    if (isRider) return "Rider Queue";
    if (isOutbound) {
      if (outboundTab === "qc_pending") return "QC Approval Queue";
      if (outboundTab === "dispatch") return "Dispatch Queue";
      return "Dispatched Orders";
    }
    return "Orders";
  }, [isSeller, isRider, isOutbound, outboundTab]);

  const pageDescription = useMemo(() => {
    if (isSeller) return "Check product availability and mark orders ready for pickup.";
    if (isRider) return "Pick up items from vendors that are ready.";
    if (isOutbound && outboundTab === "qc_pending") {
      return "Inspect picked items and approve or reject.";
    }
    if (isOutbound && outboundTab === "dispatch") {
      return "Add courier tracking and dispatch QC-approved orders.";
    }
    if (isOutbound) return "View orders that have been dispatched.";
    if (isAdmin && statusTab === "delivered") return "Delivered Orders";
    if (isAdmin && statusTab === "cancelled") return "Cancelled Orders";
    if (isAdmin && statusTab === "all") return "All Orders";
    return "Track orders through the full fulfillment pipeline.";
  }, [isSeller, isRider, isOutbound, outboundTab, isAdmin, statusTab]);

  const loadOrders = useCallback(async () => {
    if (!companyId || !userId) return;

    setLoading(true);
    try {
      const result = await fetchOrders({
        companyId,
        userId,
        userRole,
        statusTab: isSeller || isAdmin ? statusTab : undefined,
        outboundTab: isOutbound ? outboundTab : undefined,
        riderTab: isRider ? statusTab : undefined,
      });

      setOrders(result.data || []);
    } catch (err) {
      showError(err.response?.data?.error || err.message || "Failed to load orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
 // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyId,
    userId,
    userRole,
    statusTab,
    outboundTab,
    isSeller,
    isAdmin,
    isOutbound,
    isRider, 
  ]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setSelectedOrderIds([]);
  }, [statusTab, outboundTab]);

  const vendors = useMemo(() => {
    const names = new Set();
    orders.forEach((order) => {
      (order.items || []).forEach((item) => {
        names.add(getItemVendorName(item));
      });
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const marketplaces = useMemo(() => {
    const names = new Set();
    orders.forEach((order) => {
      const name = order.marketplace || order.source;
      if (name) names.add(name);
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const marketplaceOrderStatuses = useMemo(() => {
    const statuses = new Set();
    orders.forEach((order) => {
      if (order.marketplace_order_status) {
        statuses.add(order.marketplace_order_status);
      }
    });
    return [...statuses].sort((a, b) => a.localeCompare(b));
  }, [orders]);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesVendor =
        vendorFilter === "" ||
        (order.items || []).some(
          (item) => getItemVendorName(item) === vendorFilter,
        );

      if (!matchesVendor) return false;

      const matchesMarketplace =
        marketplaceFilter === "" ||
        (order.marketplace || order.source) === marketplaceFilter;

      if (!matchesMarketplace) return false;

      const matchesMarketplaceStatus =
        marketplaceStatusFilter === "" ||
        order.marketplace_order_status === marketplaceStatusFilter;

      if (!matchesMarketplaceStatus) return false;

      const orderDate = getOrderDateValue(order);
      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`);
        if (!orderDate || orderDate < from) return false;
      }
      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`);
        if (!orderDate || orderDate > to) return false;
      }

      if (!q) return true;

      const displayOrderNo = formatDisplayOrderNumber(order.id);
      const idMatch =
        String(order.id).includes(q) || displayOrderNo.includes(q);
      const marketplaceMatch = String(order.marketplace || order.source || "")
        .toLowerCase()
        .includes(q);
      const itemMatch = (order.items || []).some(
        (item) =>
          String(item.sku || "").toLowerCase().includes(q) ||
          String(item.title || "").toLowerCase().includes(q) ||
          getItemVendorName(item).toLowerCase().includes(q),
      );
      const marketplaceStatusMatch = [
        order.marketplace_order_status,
        order.marketplace_payment_status,
        order.marketplace_cancel_status,
        order.marketplace_payment_hold_status,
        order.marketplace_payment_method,
        order.marketplace_shipping_carrier,
        order.marketplace_shipping_service,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));

      return idMatch || marketplaceMatch || marketplaceStatusMatch || itemMatch;
    });
  }, [
    orders,
    search,
    vendorFilter,
    marketplaceFilter,
    marketplaceStatusFilter,
    dateFrom,
    dateTo,
  ]);

  const handleAction = async (orderId, action, extra = {}) => {
    setActionLoading(`${orderId}-${action}`);
    try {
      await updateOrderStatus({
        orderId,
        companyId,
        userId,
        action,
        ...extra,
      });
      showSuccess(`Order #${orderId} updated: ${formatStatus(action)}`);
      await loadOrders();
    } catch (err) {
      showError(
        err.response?.data?.error || err.message || "Failed to update order",
      );
    } finally {
      setActionLoading(null);
    }
  };

  const openDispatchModal = (orderId) => {
    setDispatchOrderId(orderId);
    setTrackingId("");
    setDispatchModalOpen(true);
  };

  const openProductModal = (item, order) => {
    setSelectedProduct(item);
    setSelectedProductOrder(order);
    setProductModalOpen(true);
  };

  const confirmDispatch = async () => {
    if (!trackingId.trim()) {
      showError("Courier tracking ID is required");
      return;
    }
    await handleAction(dispatchOrderId, "dispatched", {
      courierTrackingId: trackingId.trim(),
    });
    setDispatchModalOpen(false);
  };

  const setStatusTab = (tab) => {
    setSearchParams({ tab });
  };

  const setOutboundView = (view) => {
    setSearchParams({ view });
  };

  const allFilteredSelected =
    filteredOrders.length > 0 &&
    filteredOrders.every((order) => selectedOrderIds.includes(order.id));

  const handleSelectOrder = (orderId) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  const handleSelectAllOrders = () => {
    const visibleIds = filteredOrders.map((order) => order.id);

    if (allFilteredSelected) {
      setSelectedOrderIds((prev) =>
        prev.filter((id) => !visibleIds.includes(id)),
      );
      return;
    }

    setSelectedOrderIds((prev) => [...new Set([...prev, ...visibleIds])]);
  };

  const handleExportSelectedOrders = () => {
    if (selectedOrderIds.length === 0) {
      showError("Select at least one order to export");
      return;
    }

    const ordersToExport = filteredOrders.filter((order) =>
      selectedOrderIds.includes(order.id),
    );

    exportOrdersToCsv(ordersToExport, { isAdmin });  
    showSuccess(`Exported ${ordersToExport.length} order(s) to CSV`);
  };

  const renderActions = (order) => {
    const busy = (action) => actionLoading === `${order.id}-${action}`;

    if (isSeller && statusTab === "pending") {
      return (
        <div className="order-actions">
          <MDBBtn
            color="success"
            size="sm"
            disabled={!!actionLoading}
            onClick={() => handleAction(order.id, "ready_to_pick")}
          >
            {busy("ready_to_pick") ? "Updating…" : "Ready to Pick"}
          </MDBBtn>
          <MDBBtn
            color="danger"
            size="sm"
            outline
            disabled={!!actionLoading}
            onClick={() => handleAction(order.id, "not_available")}
          >
            {busy("not_available") ? "Updating…" : "Not Available"}
          </MDBBtn>
        </div>
      );
    }

    if (isRider) {
      return (
        <div className="order-actions">
          <MDBBtn
            color="primary"
            size="sm"
            disabled={!!actionLoading}
            onClick={() => handleAction(order.id, "picked")}
          >
            {busy("picked") ? "Updating…" : "Mark Picked"}
          </MDBBtn>
        </div>
      );
    }

    if (isOutbound && outboundTab === "qc_pending") {
      return (
        <div className="order-actions">
          <MDBBtn
            color="success"
            size="sm"
            disabled={!!actionLoading}
            onClick={() => handleAction(order.id, "approved")}
          >
            {busy("approved") ? "Updating…" : "Approve QC"}
          </MDBBtn>
          <MDBBtn
            color="danger"
            size="sm"
            outline
            disabled={!!actionLoading}
            onClick={() => handleAction(order.id, "rejected")}
          >
            {busy("rejected") ? "Updating…" : "Reject"}
          </MDBBtn>
        </div>
      );
    }

    if (isOutbound && outboundTab === "dispatch") {
      return (
        <div className="order-actions">
          <MDBBtn
            color="primary"
            size="sm"
            disabled={!!actionLoading}
            onClick={() => openDispatchModal(order.id)}
          >
            Dispatch
          </MDBBtn>
        </div>
      );
    }

    return null;
  };

  return (
    <MDBContainer fluid className="orders-page py-4">
      <PageWrapper title={pageTitle} description={pageDescription}>
        {(isSeller || isAdmin) && (
          <div className="orders-tabs">
            {SELLER_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`orders-tab ${statusTab === tab.key ? "active" : ""}`}
                onClick={() => setStatusTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {isOutbound && (
          <div className="orders-tabs">
            {OUTBOUND_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`orders-tab ${outboundTab === tab.key ? "active" : ""}`}
                onClick={() => setOutboundView(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {isAdmin && (
          <div className="orders-workflow-legend">
            {WORKFLOW_STEPS.map((step) => (
              <span key={step.key} className="legend-item">
                <strong>{step.label}</strong> queue
              </span>
            ))}
          </div>
        )}

        <div className="orders-toolbar">
          <input
            type="search"
            className="form-control orders-search"
            placeholder="Search by order ID, SKU, title, or vendor…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="orders-date-filters">
            <label className="orders-date-label">
              From
              <input
                type="date"
                className="form-control"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </label>
            <label className="orders-date-label">
              To
              <input
                type="date"
                className="form-control"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </label>
          </div>
          <select
            className="form-select orders-filter-select"
            aria-label="Filter by marketplace order status"
            value={marketplaceStatusFilter}
            onChange={(e) => setMarketplaceStatusFilter(e.target.value)}
          >
            <option value="">All MP Statuses</option>
            {marketplaceOrderStatuses.map((status) => (
              <option key={status} value={status}>
                {formatStatus(status)}
              </option>
            ))}
          </select>
          {isAdmin && (
            <select
              className="form-select orders-filter-select"
              aria-label="Filter by vendor"
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
            >
              <option value="">All Vendors</option>
              {vendors.map((vendor) => (
                <option key={vendor} value={vendor}>
                  {vendor}
                </option>
              ))}
            </select>
          )}
          {isAdmin && (
            <select
              className="form-select orders-filter-select"
              aria-label="Filter by marketplace"
              value={marketplaceFilter}
              onChange={(e) => setMarketplaceFilter(e.target.value)}
            >
              <option value="">All Marketplaces</option>
              {marketplaces.map((mp) => (
                <option key={mp} value={mp}>
                  {mp}
                </option>
              ))}
            </select>
          )}
          <MDBBtn color="light" onClick={loadOrders} disabled={loading}>
            Refresh
          </MDBBtn>
          <MDBBtn
            color="success"
            onClick={handleExportSelectedOrders}
            disabled={loading || selectedOrderIds.length === 0}
          >
            Export ({selectedOrderIds.length})
          </MDBBtn>
        </div>

        {selectedOrderIds.length > 0 && (
          <div className="orders-selection-bar">
            <span className="orders-selection-count">
              <strong>{selectedOrderIds.length}</strong> order(s) selected
            </span>
            <button
              type="button"
              className="orders-selection-clear"
              onClick={() => setSelectedOrderIds([])}
            >
              Clear Selection
            </button>
            <MDBBtn
              color="success"
              size="sm"
              onClick={handleExportSelectedOrders}
            >
              Export to Sheet
            </MDBBtn>
          </div>
        )}

      <MDBBtn
  color="dark"
  onClick={async () => {
    if (!userId) return;
    setSyncingOrders(true);
    try {
      // ✅ Step 0 – Force userId sync to extension before triggering scrape
      await fetch('https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userId })
      });
      console.log('✅ UserId synced with extension');

      // Wait 2 seconds for extension to re‑register with new userId
      await new Promise(resolve => setTimeout(resolve, 2000));

      // ✅ Step 1 – Now trigger the actual order sync
      await fetch('https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/trigger-sync-vinted-orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: userId })
      });

      showSuccess('Sync started! Orders will refresh shortly...');
      setTimeout(() => {
        loadOrders();
        setSyncingOrders(false);
      }, 5000);
    } catch (err) {
      showError('Sync failed: ' + err.message);
      setSyncingOrders(false);
    }
  }}
  disabled={syncingOrders}
>
  {syncingOrders ? 'Syncing…' : 'Sync Vinted Orders'}
</MDBBtn>

        {loading ? (
          <div className="orders-loading">
            <MDBSpinner role="status" />
            <span>Loading orders…</span>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="orders-empty">
            <p>No orders in this queue.</p>
          </div>
        ) : (
          <>
            <div className="orders-select-all-row">
              <input
                type="checkbox"
                id="selectAllOrders"
                checked={allFilteredSelected}
                onChange={handleSelectAllOrders}
              />
              <label htmlFor="selectAllOrders">
                Select all ({filteredOrders.length})
              </label>
            </div>

            <div className="orders-list">
            {filteredOrders.map((order) => (
              <div
                key={order.id}
                className={`order-card ${
                  selectedOrderIds.includes(order.id) ? "selected" : ""
                }`}
              >
                <div className="order-card-header">
                  <div className="order-card-header-main">
                    <div className="order-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedOrderIds.includes(order.id)}
                        onChange={() => handleSelectOrder(order.id)}
                        aria-label={`Select order #${formatDisplayOrderNumber(order.id)}`}
                      />
                    </div>
                    <div>
                    <h3 className="order-id">
                      Order #{formatDisplayOrderNumber(order.id)}
                    </h3>
                    <p className="order-meta">
                      <span className="order-marketplace-name">
                        {order.marketplace || order.source || "Marketplace"}
                      </span>
                      {order.created_at && (
                        <span className="order-meta-date">
                          {formatDateTime(order.created_at)}
                        </span>
                      )}
                    </p>
                    </div>
                  </div>
                  <div className="order-card-header-badges">
                    {isOrderDelivered(order) && (
                      <span className="outcome-badge outcome-delivered">Delivered</span>
                    )}
                    {isOrderCancelled(order) && (
                      <span className="outcome-badge outcome-cancelled">Cancelled</span>
                    )}
                    {order.courier_tracking_id ? (
                      <button
                        type="button"
                        className="tracking-badge tracking-badge-btn"
                        onClick={() => openTrackingModal(order.courier_tracking_id)}
                      >
                        Tracking: {order.courier_tracking_id}
                      </button>
                    ) : (
                      needsTrackingId(order) && (
                        <span className="tracking-badge-missing">
                          Missing Tracking ID
                        </span>
                      )
                    )}
                  </div>
                </div>

                <MDBModal open={trackingModalOpen} setOpen={setTrackingModalOpen} tabIndex="-1">
                  <MDBModalDialog centered size="lg">
                    <MDBModalContent>
                      <MDBModalHeader>
                        <MDBModalTitle>Shipment Tracking</MDBModalTitle>
                        <MDBBtn className="btn-close" color="none" onClick={() => setTrackingModalOpen(false)} />
                      </MDBModalHeader>
                      <MDBModalBody>
                        {trackingLoading && (
                          <div className="orders-loading">
                            <MDBSpinner role="status" />
                            <span>Loading tracking history…</span>
                          </div>
                        )}

                        {!trackingLoading && trackingError && (
                          <p className="text-danger">{trackingError}</p>
                        )}

                        {!trackingLoading && trackingData && (
                          <div className="tracking-timeline">
                            {trackingData.trackHistory.map((entry) => (
                              <div key={entry.id} className="tracking-timeline-item">
                                <span
                                  className="tracking-timeline-dot"
                                  style={{ backgroundColor: entry.progresses?.color_code || "#999" }}
                                />
                                <div className="tracking-timeline-content">
                                  <strong>{entry.progresses?.name}</strong>
                                  <div className="tracking-timeline-meta">
                                    {entry.date} {entry.time} · {entry.countrys?.name}
                                    {entry.city ? `, ${entry.city}` : ""}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </MDBModalBody>
                      <MDBModalFooter>
                        <MDBBtn color="secondary" onClick={() => setTrackingModalOpen(false)}>
                          Close
                        </MDBBtn>
                      </MDBModalFooter>
                    </MDBModalContent>
                  </MDBModalDialog>
                </MDBModal>

                {(isAdmin || !isSeller || statusTab !== "pending") && (
                  <OrderStatusPipeline order={order} />
                )}

                {isAdmin && <MarketplaceOrderStatus order={order} showDetails={isAdmin} />}

                <div className="order-card-body">
                  <ProductThumbnails
                    items={order.items}
                    onItemClick={(item) => openProductModal(item, order)}
                  />
               <div className="order-items-detail">
  {(order.items || []).map((item) => (
    <button
      key={item.id || item.product_id}
      type="button"
      className="order-item-row order-item-row-btn"
      onClick={() => openProductModal(item, order)}
    >
      <span className="order-item-title">
        {item.title || item.sku || `Product #${item.product_id}`}
      </span>
      <span className="order-item-meta">
        SKU: {item.sku || "—"} · Qty: {item.quantity || 1}

      {item.amount_payable != null && (
      <>
        {" · "}
        Amount Payable: {getItemAmountPayable(item, order)}
      </>
    )}

    {isAdmin && item.price != null && (
      <>
        {" · "}
        Selling Price: {getItemRawPrice(item, order)}
      </>
    )}

        {" · "}
        Vendor: {getItemVendorName(item)}
      </span>
    </button>
  ))}
</div>
                </div>

                {renderActions(order)}
              </div>
            ))}
            </div>
          </>
        )}
      </PageWrapper>

      <OrderProductModal
        item={selectedProduct}
        order={selectedProductOrder}
        isAdmin={isAdmin}
        open={productModalOpen}
        setOpen={setProductModalOpen}
      />

      <MDBModal open={dispatchModalOpen} setOpen={setDispatchModalOpen} tabIndex="-1">
        <MDBModalDialog centered>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>
                Dispatch Order #{formatDisplayOrderNumber(dispatchOrderId)}
              </MDBModalTitle>
              <MDBBtn
                className="btn-close"
                color="none"
                onClick={() => setDispatchModalOpen(false)}
              />
            </MDBModalHeader>
            <MDBModalBody>
              <MDBInput
                label="Courier Tracking ID"
                value={trackingId}
                onChange={(e) => setTrackingId(e.target.value)}
                required
              />
            </MDBModalBody>
            <MDBModalFooter>
              <MDBBtn color="secondary" onClick={() => setDispatchModalOpen(false)}>
                Cancel
              </MDBBtn>
              <MDBBtn
                color="primary"
                onClick={confirmDispatch}
                disabled={actionLoading === `${dispatchOrderId}-dispatched`}
              >
                Confirm Dispatch
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <MessageBoxComponent />
    </MDBContainer>
  );
}
